/**
 * Defines the routes for the application along with their respective components, permissions, and icons.
 *
 * @constant
 * @type {Array<Object>}
 *
 * @property {string} path - The URL path for the route.
 * @property {JSX.Element} element - The component to render for the route, wrapped with necessary providers and protections.
 * @property {string} [title] - The title of the route, used for display purposes in the navigation.
 * @property {Function} [icon] - A function that returns a JSX element representing the icon for the route.
 * @property {Array<string>} [permissions] - An array of permissions required to access the route.
 * @property {boolean} [showOnlyMobile] - A flag indicating whether the route should be shown only in mobile view.
 *
 *
 *
 * IMPORTANT!!! The titles of the routes will be translated with the t() on the Navbar component. Please do not translate them here.
 *
 *
 */
import {
    TbBell,
    TbBuildingSkyscraper,
    TbCalendar,
    TbCar,
    TbCast,
    TbFriends,
    TbHome,
    TbLayoutDashboard,
    TbMap,
    TbPlaneArrival,
    TbUsers,
} from "react-icons/tb";
import { RouteObject } from "react-router";

import { ErrorBoundary } from "@/layout/ErrorBoundary";
import CamerasPage from "@/pages/Cameras";
import DriverStatusPage from "@/pages/DriverStatus.tsx";
import HotelPlanningClinicArrivalPage from "@/pages/HotelPlanning/clinic-arrival";
import HotelPlanningDeparturePage from "@/pages/HotelPlanning/departure";
import HotelPlanningLayout from "@/pages/HotelPlanning/layout";
import LandingPage from "@/pages/Landing";
import MapPage from "@/pages/Map";
import { NotFoundPage } from "@/pages/NotFound";
import NotificationsPage from "@/pages/Notifications";
import PlannPage from "@/pages/Plannn";
import { QrCodePage } from "@/pages/Qr";
import VehiclesPage from "@/pages/Vehicles";
import WelcomeTeamPage from "@/pages/WelcomeTeam";
import TrackingPage from "@/pages/tracking";
import UsersPage from "@/pages/users";
import { ProtectedRoute } from "@/providers/hooks/useProtectedRoute";

import { PERMISSIONS } from "./permissions";

const pagePermissions = {
    dashboard: [PERMISSIONS.TR_SVC_PASSENGERS_LIST_BY_DATE, PERMISSIONS.TR_SVC_PASSENGERS_SET_DATA],
    backOfficeDashboard: [
        PERMISSIONS.TR_SVC_PASSENGERS_LIST_BY_DATE,
        PERMISSIONS.TR_SVC_BACK_OFFICE_PASSENGERS_SET_DATA,
    ],
    tracking: [
        PERMISSIONS.TR_SVC_PASSENGERS_LIST_BY_DATE,
        PERMISSIONS.TR_SVC_DATA_GRID_SAVE,
        PERMISSIONS.TR_SVC_MANAGE_DRIVERS,
    ],
    plan: [PERMISSIONS.TR_SVC_PASSENGERS_LIST_BY_DATE],
    backOfficePlan: [PERMISSIONS.TR_SVC_PASSENGERS_LIST_BY_DATE, PERMISSIONS.TR_SVC_BACK_OFFICE_PASSENGERS_SET_DATA],
    map: [PERMISSIONS.TR_SVC_VEHICLES_LIST],
    welcomeTeam: [PERMISSIONS.TR_SVC_GET_TASKS_ARRIVALS, PERMISSIONS.TR_SVC_WELCOME_TEAM_PASSENGERS_SET_DATA],
    cameras: [PERMISSIONS.TR_SVC_DRIVERS_CAMERAS],
    notifications: [],
    teamsManagement: [PERMISSIONS.TR_SVC_MANAGE_DRIVERS],
    humanResources: [PERMISSIONS.TR_SVC_HR_LIST_DAILY_STATUS],
    vehicles: [PERMISSIONS.TR_SVC_VEHICLES_PAGE],
    driverStatus: [PERMISSIONS.TR_SVC_MANAGE_DRIVERS],
    hotelPlanning: [PERMISSIONS.TR_SVC_HR_LIST_HOTEL_PLANNING],
};

const routes: (RouteObject & {
    hidden?: boolean;
    hideInMobile?: boolean;
    hideInSidebar?: boolean;
    mobileTitle?: string;
    title?: string;
    icon?: () => React.ReactNode;
    permissions?: string[];
    path: string;
})[] = [
    {
        path: "/",
        element: (
            <ErrorBoundary>
                <ProtectedRoute>
                    <LandingPage />
                </ProtectedRoute>
            </ErrorBoundary>
        ),
        icon: () => <TbHome size={25} />,
        mobileTitle: "Ana Sayfa",
    },
    {
        path: "/new-tracking",
        element: (
            <ErrorBoundary>
                <ProtectedRoute permissions={pagePermissions.dashboard}>
                    <TrackingPage />
                </ProtectedRoute>
            </ErrorBoundary>
        ),
        title: "Takip",
        icon: () => <TbLayoutDashboard size={25} />,
        permissions: pagePermissions.tracking,
    },
    // {
    //     path: "/dashboard",
    //     element: (
    //         <ErrorBoundary>
    //             <ProtectedRoute permissions={pagePermissions.dashboard}>
    //                 <DashboardPage />
    //             </ProtectedRoute>
    //         </ErrorBoundary>
    //     ),
    //     title: "Dashboard",
    //     icon: () => <TbChartBar size={25} />,
    //     permissions: pagePermissions.dashboard,
    // },
    {
        path: "/plan",
        element: (
            <ErrorBoundary>
                <ProtectedRoute permissions={pagePermissions.plan}>
                    <PlannPage />
                </ProtectedRoute>
            </ErrorBoundary>
        ),
        title: "Plan",
        icon: () => <TbCalendar size={25} />,
        permissions: pagePermissions.plan,
    },

    {
        path: "/map",
        element: (
            <ErrorBoundary>
                <ProtectedRoute permissions={pagePermissions.map}>
                    <MapPage />
                </ProtectedRoute>
            </ErrorBoundary>
        ),
        title: "map",
        icon: () => <TbMap size={25} />,
        permissions: pagePermissions.map,
    },
    {
        path: "/welcome-team",
        element: (
            <ErrorBoundary>
                <ProtectedRoute permissions={pagePermissions.welcomeTeam}>
                    <WelcomeTeamPage />
                </ProtectedRoute>
            </ErrorBoundary>
        ),
        title: "welcome_team",
        icon: () => <TbPlaneArrival size={25} />,
        permissions: pagePermissions.welcomeTeam,
    },
    {
        path: "/hastane-otel",
        element: (
            <ErrorBoundary>
                <ProtectedRoute permissions={pagePermissions.driverStatus}>
                    <HotelPlanningLayout />
                </ProtectedRoute>
            </ErrorBoundary>
        ),
        title: "hotelPlanning",
        icon: () => <TbBuildingSkyscraper size={25} />,
        permissions: pagePermissions.hotelPlanning,
        children: [
            // {
            //     index: true,
            //     element: <HotelPlanningArrivalPage />,
            // },
            {
                index: true,
                element: <HotelPlanningClinicArrivalPage />,
            },
            {
                path: "departure",
                element: <HotelPlanningDeparturePage />,
            },
        ],
    },
    {
        path: "/cameras",
        element: (
            <ErrorBoundary>
                <ProtectedRoute permissions={pagePermissions.cameras}>
                    <CamerasPage />
                </ProtectedRoute>
            </ErrorBoundary>
        ),
        title: "cameras",
        hideInMobile: true,
        icon: () => <TbCast size={25} />,
        permissions: pagePermissions.cameras,
    },
    {
        path: "/users",
        element: (
            <ErrorBoundary>
                <ProtectedRoute permissions={pagePermissions.driverStatus}>
                    <UsersPage />
                </ProtectedRoute>
            </ErrorBoundary>
        ),
        title: "users",
        icon: () => <TbUsers size={25} />,
        permissions: pagePermissions.driverStatus,
    },
    {
        path: "/vehicles",
        element: (
            <ErrorBoundary>
                <ProtectedRoute permissions={pagePermissions.vehicles}>
                    <VehiclesPage />
                </ProtectedRoute>
            </ErrorBoundary>
        ),
        title: "vehicles",
        icon: () => <TbCar size={25} />,
        permissions: pagePermissions.vehicles,
    },
    {
        path: "/notifications",
        element: (
            <ErrorBoundary>
                <ProtectedRoute permissions={pagePermissions.notifications}>
                    <NotificationsPage />
                </ProtectedRoute>
            </ErrorBoundary>
        ),
        icon: () => <TbBell size={25} />,
        hideInSidebar: true,
        permissions: pagePermissions.notifications,
        hidden: true,
    },
    {
        path: "/staff",
        element: (
            <ErrorBoundary>
                <ProtectedRoute permissions={pagePermissions.driverStatus}>
                    <DriverStatusPage />
                </ProtectedRoute>
            </ErrorBoundary>
        ),
        title: "staff",
        icon: () => <TbFriends size={25} />,
        permissions: pagePermissions.humanResources,
    },
    {
        path: "/qr",
        element: (
            <ErrorBoundary>
                <QrCodePage />
            </ErrorBoundary>
        ),
        hideInSidebar: true,
    },
    {
        path: "*",
        element: (
            <ErrorBoundary>
                <ProtectedRoute>
                    <NotFoundPage />
                </ProtectedRoute>
            </ErrorBoundary>
        ),
    },
];

export default routes;
